/**
 * Staffelhint: "bij 50 stuks betaal je 0,42 per stuk, 31% minder per stuk".
 *
 * Alleen uit echte, al opgehaalde staffelprijzen in dezelfde levertijdkolom;
 * nooit een geschatte korting. Alleen een volgende stap die echt scheelt
 * (standaard minstens 10% per stuk) en niet absurd veel groter is dan wat de
 * klant koos (max. 5x), tenzij het totaal er nauwelijks door stijgt (max. 25%
 * meer, zoals 1 flyer voor 58 euro tegen 10 voor 59 euro). De tip noemt altijd
 * wat het in totaal meer kost.
 */
export type NudgeCell = { available: boolean; unitInclVat: number; totalInclVat: number };
export type NudgeRow = { quantity: number; cells: Record<string, NudgeCell | undefined> };

export type QuantityNudge = {
  quantity: number;
  unitInclVat: number;
  totalInclVat: number;
  /** Besparing per stuk ten opzichte van het gekozen aantal, naar beneden afgerond. */
  savingPercent: number;
  /** Wat de klant er in totaal bij betaalt. */
  extraInclVat: number;
};

export function findQuantityNudge(
  rows: NudgeRow[],
  selectedQuantity: number,
  columnId: string,
  options: { minSavingPercent?: number; maxFactor?: number; maxExtraShare?: number } = {},
): QuantityNudge | null {
  const minSaving = options.minSavingPercent ?? 10;
  const maxFactor = options.maxFactor ?? 5;
  const maxExtraShare = options.maxExtraShare ?? 0.25;
  const current = rows.find((row) => row.quantity === selectedQuantity)?.cells[columnId];
  if (!current?.available || !(current.unitInclVat > 0) || !(current.totalInclVat > 0)) return null;
  const candidates = rows
    .filter((row) => row.quantity > selectedQuantity)
    .sort((a, b) => a.quantity - b.quantity);
  for (const row of candidates) {
    const cell = row.cells[columnId];
    if (!cell?.available || !(cell.unitInclVat > 0) || !(cell.totalInclVat > current.totalInclVat)) continue;
    const smallStep = row.quantity <= selectedQuantity * maxFactor;
    const cheapStep = cell.totalInclVat - current.totalInclVat <= current.totalInclVat * maxExtraShare;
    if (!smallStep && !cheapStep) continue;
    const savingPercent = Math.floor((1 - cell.unitInclVat / current.unitInclVat) * 100);
    if (savingPercent < minSaving) continue;
    return {
      quantity: row.quantity,
      unitInclVat: cell.unitInclVat,
      totalInclVat: cell.totalInclVat,
      savingPercent,
      extraInclVat: Number((cell.totalInclVat - current.totalInclVat).toFixed(2)),
    };
  }
  return null;
}

const COPY = {
  nl: { text: 'Tip: {quantity} stuks kost {extra} meer en dan betaal je {unit} per stuk ({percent}% minder per stuk).', cta: 'Kies {quantity} stuks' },
  en: { text: 'Tip: {quantity} pieces cost {extra} more, and you pay {unit} each ({percent}% less per piece).', cta: 'Choose {quantity} pieces' },
  de: { text: 'Tipp: {quantity} Stück kosten {extra} mehr, dann zahlen Sie {unit} pro Stück ({percent}% weniger pro Stück).', cta: '{quantity} Stück wählen' },
  fr: { text: 'Astuce : {quantity} pièces coûtent {extra} de plus, et vous payez {unit} la pièce ({percent} % de moins par pièce).', cta: 'Choisir {quantity} pièces' },
} as const;

export function quantityNudgeCopy(locale: string, nudge: QuantityNudge, formattedUnit: string, formattedExtra: string) {
  const copy = COPY[String(locale || 'nl').slice(0, 2) as keyof typeof COPY] || COPY.en;
  const fill = (text: string) => text
    .replace('{quantity}', String(nudge.quantity))
    .replace('{unit}', formattedUnit)
    .replace('{extra}', formattedExtra)
    .replace('{percent}', String(nudge.savingPercent));
  return { text: fill(copy.text), cta: fill(copy.cta) };
}
